import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { BottomNav } from '../components/custom/BottomNav';
import { TaskCard } from '../components/custom/TaskCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Search, Filter, FolderOpen, DollarSign, Wallet } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BILLING_TYPES = [
    { value: "hourly", label: "Hourly", description: "Bill by the hour, deducts from retainer" },
    { value: "flat_fee", label: "Flat Fee", description: "Fixed price, no retainer deduction" },
    { value: "contingency", label: "Contingency", description: "Percentage of settlement" },
    { value: "retainer", label: "Retainer Only", description: "Prepaid hours" },
];

export default function CasesPage() {
    const [cases, setCases] = useState([]);
    const [caseTypes, setCaseTypes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [newCaseName, setNewCaseName] = useState('');
    const [newClientName, setNewClientName] = useState('');
    const [newCaseType, setNewCaseType] = useState('Other');
    const [newBillingType, setNewBillingType] = useState('hourly');
    const [newHourlyRate, setNewHourlyRate] = useState('');
    const [newFlatFee, setNewFlatFee] = useState('');
    const [newRetainer, setNewRetainer] = useState('');
    const [defaultHourlyRate, setDefaultHourlyRate] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { getAuthHeaders } = useAuth();
    const navigate = useNavigate();

    const fetchCases = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/cases`, {
                headers: getAuthHeaders()
            });
            setCases(response.data);
        } catch (error) {
            toast.error('Failed to load cases');
        } finally {
            setIsLoading(false);
        }
    }, [getAuthHeaders]);

    const fetchCaseTypes = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/case-types`, {
                headers: getAuthHeaders()
            });
            const types = response.data.case_types || [];
            // Sort alphabetically, keeping "Other" at the end
            const sortedTypes = types.filter(t => t !== 'Other').sort();
            if (types.includes('Other')) sortedTypes.push('Other');
            setCaseTypes(sortedTypes.length > 0 ? sortedTypes : ["Contract Law", "Criminal Law", "Employment Law", "Family Law", "Immigration Law", "Personal Injury Law", "Other"]);
        } catch {
            // Use fallback case types (alphabetically sorted)
            setCaseTypes(["Contract Law", "Criminal Law", "Employment Law", "Family Law", "Immigration Law", "Personal Injury Law", "Other"]);
        }
    }, [getAuthHeaders]);

    const fetchUserSettings = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/auth/me`, {
                headers: getAuthHeaders()
            });
            setDefaultHourlyRate(response.data.default_hourly_rate || 0);
        } catch {
            // Ignore
        }
    }, [getAuthHeaders]);

    useEffect(() => {
        fetchCases();
        fetchCaseTypes();
        fetchUserSettings();
    }, [fetchCases, fetchCaseTypes, fetchUserSettings]);

    const handleCreateCase = async () => {
        if (!newCaseName.trim()) {
            toast.error('Case name is required');
            return;
        }
        setIsSubmitting(true);
        try {
            const caseData = {
                name: newCaseName,
                client_name: newClientName,
                case_type: newCaseType,
                billing_type: newBillingType,
                hourly_rate: parseFloat(newHourlyRate) || null,
                retainer_amount: parseFloat(newRetainer) || 0,
            };
            if (newBillingType === 'flat_fee') {
                caseData.flat_fee_amount = parseFloat(newFlatFee) || 0;
            }
            await axios.post(`${API}/cases`, caseData, {
                headers: getAuthHeaders()
            });
            toast.success('Case created');
            resetCreateForm();
            setIsCreateOpen(false);
            fetchCases();
        } catch (error) {
            toast.error('Failed to create case');
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetCreateForm = () => {
        setNewCaseName('');
        setNewClientName('');
        setNewCaseType('Other');
        setNewBillingType('hourly');
        setNewHourlyRate('');
        setNewFlatFee('');
        setNewRetainer('');
    };

    const handleOpenCreate = () => {
        resetCreateForm();
        setNewHourlyRate(defaultHourlyRate > 0 ? defaultHourlyRate.toString() : '');
        setIsCreateOpen(true);
    };

    const formatMoney = (amount) => {
        if (!amount || amount === 0) return null;
        return `$${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    };

    const formatTime = (minutes) => {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        if (hours > 0) return `${hours}h ${mins}m`;
        return `${mins}m`;
    };

    // Filter cases
    const filteredCases = cases.filter(c => {
        const matchesSearch = c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                             c.client_name?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesStatus = filterStatus === 'all' || c.status === filterStatus;
        return matchesSearch && matchesStatus;
    });

    return (
        <div className="min-h-screen bg-background pb-safe">
            {/* Header */}
            <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border/50">
                <div className="px-6 py-4">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate('/dashboard')}
                            className="p-2 -ml-2 hover:bg-secondary rounded-full transition-colors"
                        >
                            <ArrowLeft className="h-5 w-5" />
                        </button>
                        <h1 className="font-heading text-2xl font-semibold">Cases</h1>
                        <div className="flex-1" />
                        <button
                            onClick={handleOpenCreate}
                            className="p-3 bg-primary text-white rounded-full shadow-lg hover:shadow-xl transition-all active:scale-95"
                            data-testid="create-case-btn"
                        >
                            <Plus className="h-5 w-5" />
                        </button>
                    </div>

                    {/* Search & Filter */}
                    <div className="flex gap-3 mt-4">
                        <div className="flex-1 relative">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Search cases..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-11 h-12 rounded-xl bg-white"
                                data-testid="search-input"
                            />
                        </div>
                        <Select value={filterStatus} onValueChange={setFilterStatus}>
                            <SelectTrigger className="w-32 h-12 rounded-xl bg-white">
                                <Filter className="h-4 w-4 mr-2" />
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All</SelectItem>
                                <SelectItem value="Open">Open</SelectItem>
                                <SelectItem value="In Progress">In Progress</SelectItem>
                                <SelectItem value="Pending">Pending</SelectItem>
                                <SelectItem value="Closed">Closed</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </header>

            <main className="px-6 py-4">
                {isLoading ? (
                    <div className="space-y-3">
                        {[1, 2, 3, 4, 5].map(i => (
                            <div key={i} className="h-20 bg-white rounded-3xl animate-pulse" />
                        ))}
                    </div>
                ) : filteredCases.length > 0 ? (
                    <div className="space-y-3">
                        {filteredCases.map(caseItem => (
                            <div 
                                key={caseItem.id}
                                onClick={() => navigate(`/case/${caseItem.id}`)}
                                className={`rounded-2xl p-4 shadow-soft cursor-pointer hover:shadow-md transition-all active:scale-[0.99] ${
                                    caseItem.is_shared 
                                        ? 'bg-blue-50/70 border border-blue-100' 
                                        : 'bg-white'
                                }`}
                                data-testid={`case-card-${caseItem.id}`}
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <h3 className="font-medium truncate">{caseItem.name}</h3>
                                            {caseItem.active_timer && (
                                                <Badge className="bg-indigo-100 text-indigo-700 border-0 text-xs">Running</Badge>
                                            )}
                                            {caseItem.is_shared && (
                                                <Badge className="bg-blue-100 text-blue-700 border-0 text-xs">{caseItem.owner_name}</Badge>
                                            )}
                                        </div>
                                        <p className="text-sm text-muted-foreground truncate">
                                            {caseItem.client_name || caseItem.case_type}
                                        </p>
                                        <div className="flex items-center gap-3 mt-2">
                                            <span className="text-xs text-muted-foreground">
                                                {formatTime(caseItem.total_minutes)}
                                            </span>
                                            {caseItem.billing_type === 'flat_fee' && (
                                                <Badge variant="outline" className="text-xs bg-blue-50 text-[#1C6EE7] border-blue-200">
                                                    Flat Fee
                                                </Badge>
                                            )}
                                            {caseItem.retainer_balance > 0 && caseItem.billing_type !== 'flat_fee' && (
                                                <span className="text-xs text-emerald-600 flex items-center gap-1">
                                                    <Wallet className="h-3 w-3" />
                                                    {formatMoney(caseItem.retainer_balance)} left
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <Badge className={`${
                                        caseItem.status === 'Open' ? 'bg-emerald-100 text-emerald-700' :
                                        caseItem.status === 'In Progress' ? 'bg-blue-100 text-blue-700' :
                                        caseItem.status === 'Pending' ? 'bg-amber-100 text-amber-700' :
                                        'bg-slate-100 text-slate-700'
                                    } border-0`}>
                                        {caseItem.status}
                                    </Badge>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-16">
                        <FolderOpen className="h-16 w-16 text-slate-200 mx-auto mb-4" />
                        <h3 className="font-heading text-xl font-semibold text-muted-foreground mb-2">
                            {searchQuery ? 'No cases found' : 'No cases yet'}
                        </h3>
                        <p className="text-sm text-muted-foreground mb-6">
                            {searchQuery ? 'Try a different search term' : 'Create your first case to start tracking time'}
                        </p>
                        {!searchQuery && (
                            <Button
                                onClick={handleOpenCreate}
                                className="rounded-full px-6"
                            >
                                <Plus className="h-4 w-4 mr-2" />
                                Create Case
                            </Button>
                        )}
                    </div>
                )}

                {/* Spacer for bottom nav */}
                <div className="h-24" />
            </main>

            <BottomNav onQuickAdd={handleOpenCreate} />

            {/* Create Case Dialog */}
            <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
                <DialogContent className="max-h-[85vh] overflow-y-auto max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl">New Case</DialogTitle>
                        <DialogDescription>Create a new case with billing settings</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        {/* Basic Info */}
                        <div className="space-y-2">
                            <Label>Case Name *</Label>
                            <Input
                                placeholder="Smith vs. Johnson"
                                value={newCaseName}
                                onChange={(e) => setNewCaseName(e.target.value)}
                                className="rounded-xl h-12"
                                data-testid="case-name-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Client Name</Label>
                            <Input
                                placeholder="John Smith"
                                value={newClientName}
                                onChange={(e) => setNewClientName(e.target.value)}
                                className="rounded-xl h-12"
                                data-testid="client-name-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Case Type</Label>
                            <Select value={newCaseType} onValueChange={setNewCaseType}>
                                <SelectTrigger className="rounded-xl h-12" data-testid="case-type-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="max-h-[40vh] overflow-y-auto">
                                    {caseTypes.length > 0 ? caseTypes.map(t => (
                                        <SelectItem key={t} value={t}>{t}</SelectItem>
                                    )) : (
                                        <SelectItem value="Other">Other</SelectItem>
                                    )}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Billing Section */}
                        <div className="pt-4 border-t">
                            <div className="flex items-center gap-2 mb-4">
                                <DollarSign className="h-4 w-4 text-emerald-600" />
                                <span className="font-medium">Billing</span>
                            </div>
                            
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label>Billing Type</Label>
                                    <Select value={newBillingType} onValueChange={setNewBillingType}>
                                        <SelectTrigger className="rounded-xl h-12" data-testid="billing-type-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {BILLING_TYPES.map(bt => (
                                                <SelectItem key={bt.value} value={bt.value}>
                                                    <div className="flex flex-col">
                                                        <span>{bt.label}</span>
                                                    </div>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <p className="text-xs text-muted-foreground">
                                        {BILLING_TYPES.find(bt => bt.value === newBillingType)?.description}
                                    </p>
                                </div>

                                {(newBillingType === 'hourly' || newBillingType === 'retainer') && (
                                    <div className="space-y-2">
                                        <Label>Hourly Rate ($)</Label>
                                        <Input
                                            type="number"
                                            placeholder={defaultHourlyRate > 0 ? `Default: $${defaultHourlyRate}` : "e.g., 350"}
                                            value={newHourlyRate}
                                            onChange={(e) => setNewHourlyRate(e.target.value)}
                                            className="rounded-xl h-12 font-display"
                                            data-testid="hourly-rate-input"
                                        />
                                    </div>
                                )}

                                {newBillingType === 'flat_fee' && (
                                    <div className="space-y-2">
                                        <Label>Flat Fee Amount ($)</Label>
                                        <Input
                                            type="number"
                                            placeholder="e.g., 5000"
                                            value={newFlatFee}
                                            onChange={(e) => setNewFlatFee(e.target.value)}
                                            className="rounded-xl h-12 font-display"
                                            data-testid="flat-fee-input"
                                        />
                                    </div>
                                )}

                                {newBillingType !== 'contingency' && (
                                    <div className="space-y-2">
                                        <Label className="flex items-center gap-2">
                                            <Wallet className="h-4 w-4 text-[#1C6EE7]" />
                                            Initial Retainer ($)
                                        </Label>
                                        <Input
                                            type="number"
                                            placeholder="e.g., 5000"
                                            value={newRetainer}
                                            onChange={(e) => setNewRetainer(e.target.value)}
                                            className="rounded-xl h-12 font-display"
                                            data-testid="retainer-input"
                                        />
                                        {newBillingType !== 'flat_fee' && (
                                            <p className="text-xs text-muted-foreground">
                                                Billed time will be deducted from this balance
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <Button
                            onClick={handleCreateCase}
                            disabled={isSubmitting || !newCaseName}
                            className="w-full h-14 rounded-full text-lg font-semibold"
                            data-testid="submit-case-btn"
                        >
                            {isSubmitting ? 'Creating...' : 'Create Case'}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
